import { useEffect, useState } from "react";
import { supabase } from "../../../../lib/supabase";
import { useAuth } from "../../../../hooks/useAuth";
import { useToast } from "../../../../hooks/useToast";
import DashboardLayout from "../../../../components/feature/DashboardLayout";

interface ClientProfile {
  id: string;
  full_name: string | null;
  phone: string | null;
  address: string | null;
  created_at: string;
  care_plan_count?: number;
  appointment_count?: number;
}

const navItems = [
  { label: "Dashboard", path: "/dashboard/admin", icon: "ri-dashboard-line" },
  { label: "Clients", path: "/dashboard/admin/clients", icon: "ri-user-line" },
  { label: "Caregivers", path: "/dashboard/admin/caregivers", icon: "ri-user-heart-line" },
  { label: "Appointments", path: "/dashboard/admin/appointments", icon: "ri-calendar-event-line" },
  { label: "Care Plans", path: "/dashboard/admin/care-plans", icon: "ri-file-list-3-line" },
  { label: "Settings", path: "/dashboard/admin/settings", icon: "ri-settings-3-line" },
];

export default function AdminClients() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [clients, setClients] = useState<ClientProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState<ClientProfile | null>(null);

  useEffect(() => {
    if (!user) return;
    fetchClients();

    // Real-time subscription for profiles
    const channel = supabase
      .channel('profiles-admin')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'profiles',
      }, (payload) => {
        fetchClients();
        if (payload.eventType === 'INSERT') showToast('New client registered', 'success');
        if (payload.eventType === 'UPDATE') showToast('Client profile updated', 'info');
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const fetchClients = async () => {
    const { data: profiles } = await supabase
      .from("profiles")
      .select("*")
      .eq("role", "client")
      .order("created_at", { ascending: false });

    const clientList = (profiles || []).map((p: any) => ({
      id: p.id,
      full_name: p.full_name,
      phone: p.phone,
      address: p.address,
      created_at: p.created_at,
      care_plan_count: 0,
      appointment_count: 0,
    }));

    // Fetch counts for each client
    for (const client of clientList) {
      const { count: planCount } = await supabase
        .from("care_plans")
        .select("*", { count: "exact", head: true })
        .eq("client_id", client.id);
      const { count: apptCount } = await supabase
        .from("appointments")
        .select("*", { count: "exact", head: true })
        .eq("client_id", client.id);
      client.care_plan_count = planCount || 0;
      client.appointment_count = apptCount || 0;
    }

    setClients(clientList);
    setLoading(false);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const filteredClients = clients.filter((c) => {
    const term = search.toLowerCase();
    return (
      (c.full_name?.toLowerCase() || "").includes(term) ||
      (c.phone || "").includes(term) ||
      (c.address?.toLowerCase() || "").includes(term)
    );
  });

  return (
    <DashboardLayout navItems={navItems} title="Admin Portal" subtitle="All Clients">
      {loading ? (
        <div className="flex items-center gap-3 py-12">
          <div className="w-5 h-5 border-2 border-brand-green-600 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-gray-500">Loading clients...</span>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Search */}
          <div className="flex items-center gap-3">
            <div className="flex-1 relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                <i className="ri-search-line text-gray-400" />
              </div>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, phone, or address..."
                className="w-full pl-9 pr-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none"
              />
            </div>
            <div className="text-sm text-gray-500 whitespace-nowrap">
              {filteredClients.length} client{filteredClients.length !== 1 ? "s" : ""}
            </div>
          </div>

          {/* Clients Table / Cards */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Client</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Phone</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Address</th>
                    <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Plans</th>
                    <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Visits</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Joined</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredClients.map((client) => (
                    <tr key={client.id} className="hover:bg-soft-gray transition-colors">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-brand-green-100 flex items-center justify-center shrink-0">
                            <div className="w-5 h-5 flex items-center justify-center">
                              <i className="ri-user-line text-brand-green-600" />
                            </div>
                          </div>
                          <span className="text-sm font-medium text-gray-900">{client.full_name || "Unnamed"}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-sm text-gray-600">{client.phone || "—"}</td>
                      <td className="px-5 py-3 text-sm text-gray-600 max-w-[200px] truncate">{client.address || "—"}</td>
                      <td className="px-5 py-3 text-center">
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-brand-green-50 text-brand-green-700">{client.care_plan_count}</span>
                      </td>
                      <td className="px-5 py-3 text-center">
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">{client.appointment_count}</span>
                      </td>
                      <td className="px-5 py-3 text-sm text-gray-500">{formatDate(client.created_at)}</td>
                      <td className="px-5 py-3">
                        <button
                          onClick={() => setSelectedClient(client)}
                          className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-eye-line text-gray-500" />
                          </div>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {filteredClients.map((client) => (
                <div key={client.id} className="p-4" onClick={() => setSelectedClient(client)}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-brand-green-100 flex items-center justify-center shrink-0">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-user-line text-brand-green-600" />
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{client.full_name || "Unnamed"}</p>
                      <p className="text-xs text-gray-500">{client.phone || "No phone"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span className="px-2 py-0.5 rounded-full bg-brand-green-50 text-brand-green-700">{client.care_plan_count} plans</span>
                    <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">{client.appointment_count} visits</span>
                  </div>
                </div>
              ))}
            </div>

            {filteredClients.length === 0 && (
              <div className="text-center py-12">
                <div className="w-14 h-14 rounded-full bg-soft-gray flex items-center justify-center mx-auto mb-3">
                  <div className="w-7 h-7 flex items-center justify-center">
                    <i className="ri-user-line text-xl text-gray-400" />
                  </div>
                </div>
                <p className="text-sm text-gray-500">No clients found.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Client Detail Modal */}
      {selectedClient && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setSelectedClient(null)} />
          <div className="relative bg-white rounded-2xl p-6 w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-semibold text-gray-900">Client Details</h3>
              <button onClick={() => setSelectedClient(null)} className="p-1 rounded-lg hover:bg-gray-100">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-close-line text-gray-500" />
                </div>
              </button>
            </div>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-brand-green-100 flex items-center justify-center">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-user-line text-brand-green-600 text-lg" />
                  </div>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">{selectedClient.full_name || "Unnamed"}</p>
                  <p className="text-xs text-gray-500">ID: {selectedClient.id.slice(0, 8)}...</p>
                </div>
              </div>
              <div className="space-y-3 pt-3 border-t border-gray-100">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Phone</span>
                  <span className="text-sm text-gray-900">{selectedClient.phone || "—"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Address</span>
                  <span className="text-sm text-gray-900 text-right max-w-[180px]">{selectedClient.address || "—"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Joined</span>
                  <span className="text-sm text-gray-900">{formatDate(selectedClient.created_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Care Plans</span>
                  <span className="text-sm text-gray-900">{selectedClient.care_plan_count}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Appointments</span>
                  <span className="text-sm text-gray-900">{selectedClient.appointment_count}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}